import React, { Fragment, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { Pagination } from "@mui/material";
import { CallAPI } from "../../utils/apiActions";
import { LABEL, paginationObj } from "../../utils/constant";
import {
  QNA,
  SAMPLE_QNA_PROMPT_DOWNLOAD,
  SAMPLE_QNA_PROMPT_GET,
} from "../../utils/APIEndPoints";
import { showToast } from "../../helper/toastHelper";
import usePermissions from "../../hooks/usePermissions";
import SplashScreen from "../SplashScreen";
import { useCallAPI } from "../../hooks/useCallAPI";

const initialFormData = {
  question: "",
  answer: "",
  questionErr: "",
  answerErr: "",
};

export default function BotQnAPrompt({ ...props }) {
  const location = useLocation();
  const [modalOpen, setModalOpen] = useState(false);
  const [submissionLoader, setSubmissionLoader] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const bot_id = new URLSearchParams(location?.search).get("id") || null;
  const [QnAList, setQnAList] = useState([]);
  const [paginationData, SetPaginationData] = useState({
    totalRecords: 0,
    currentPage: 1,
    totalPages: 0,
    pageSize: paginationObj.item_per_page,
  });
  const textareaRefs = useRef({});
  const permission = usePermissions("my-chatbot");
  const [data, setData] = useState({});

  const totalRecords = paginationData.totalRecords || 0;
  const paginationStart =
    totalRecords > 0
      ? (paginationData.currentPage - 1) * paginationData.pageSize + 1
      : 0;
  const paginationEnd =
    totalRecords > 0
      ? Math.min(
          paginationData.currentPage * paginationData.pageSize,
          totalRecords,
        )
      : 0;

  const {
    data: sampleRes,
    loading: sampleLoading,
    revalidate: getSampleQnAPrompt,
  } = useCallAPI({
    end_point: "USER",
    method: "GET",
    apiUrl: SAMPLE_QNA_PROMPT_GET,
    enabled: false,
  });

  const {
    data: qnaData,
    loading: qnaLoading,
    revalidate: fetchQnA,
  } = useCallAPI({
    end_point: "USER",
    method: "GET",
    apiUrl: `${QNA}/${bot_id}?page=1&pageSize=${paginationData.pageSize}`,
    enabled: false,
  });

  const handleInput = (name) => {
    const textarea = textareaRefs.current[name];
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  const transformArrayToObject = (items) => {
    return items.reduce((acc, item) => {
      acc[item.file_type] = { link: item.file_link, id: item.id };
      return acc;
    }, {});
  };

  const resizeTextareas = () => {
    if (textareaRefs.current.answer) {
      textareaRefs.current.answer.style.height = "auto";
      textareaRefs.current.answer.style.height = `${textareaRefs.current.answer.scrollHeight}px`;
    }
  };

  const applyQnAResponse = (response) => {
    if (!response?.status) return;
    if (!response?.res?.data?.dataObj) return;
    const pagination = response?.res?.data?.dataObj?.pagination || {};

    setQnAList(response?.res?.data?.dataObj?.qnaArray || []);
    SetPaginationData((prev) => ({
      ...prev,
      currentPage: pagination?.currentPage || prev.currentPage,
      pageSize: pagination?.pageSize || prev.pageSize,
      totalRecords: pagination?.totalRecords || 0,
      totalPages: pagination?.totalPages || 0,
    }));
    props.setFinalStep(pagination?.completedStep || 2);
  };

  const getAllList = (
    currentPage = paginationData.currentPage,
    pageSize = paginationData.pageSize,
  ) => {
    const pageQuery = `?page=${currentPage}&pageSize=${pageSize}`;
    return fetchQnA(null, `${QNA}/${bot_id}${pageQuery}`);
  };

  useEffect(() => {
    getSampleQnAPrompt();
  }, []);

  useEffect(() => {
    if (!sampleRes?.status) return;

    const payload = sampleRes?.res?.data?.data;
    if (!payload) return;

    setData(transformArrayToObject(payload || []));
  }, [sampleRes]);

  useEffect(() => {
    if (modalOpen) {
      setTimeout(resizeTextareas, 100);
    }
  }, [modalOpen]);

  useEffect(() => {
    getAllList(paginationData.currentPage, paginationData.pageSize);
  }, [paginationData.currentPage, paginationData.pageSize]);

  useEffect(() => {
    applyQnAResponse(qnaData);
  }, [qnaData]);

  const handleOnModalClose = () => {
    setModalOpen(false);
  };

  const handleOpenAddModal = () => {
    setModalOpen(true);
    setFormData(initialFormData);
  };

  const handleOpenEditModal = (item) => {
    setFormData({
      id: item.id,
      question: item.question,
      answer: item.answer,
      questionErr: "",
      answerErr: "",
    });
    setModalOpen(true);
  };

  const handleAddUpdate = async () => {
    let validationError = false;
    const nextFormData = { ...formData };

    if (!formData.question) {
      nextFormData.questionErr = "Question is required";
      validationError = true;
    } else {
      nextFormData.questionErr = "";
    }

    if (!formData.answer) {
      nextFormData.answerErr = "Answer is required";
      validationError = true;
    } else {
      nextFormData.answerErr = "";
    }

    setFormData(nextFormData);

    if (validationError) return;

    setSubmissionLoader(true);
    const body = {
      bot_id,
      step: 3,
      type: "qna",
      qnaArray: [
        {
          id: formData.id,
          question: formData.question,
          answer: formData.answer,
        },
      ],
    };

    const response = await CallAPI("USER", "POST", QNA, body);
    if (response.status) {
      showToast(response.msg, LABEL.TOAST_TYPE.SUCCESS);
      const refreshedResponse = await getAllList(1, paginationData.pageSize);
      applyQnAResponse(refreshedResponse);
      SetPaginationData((prev) => ({
        ...prev,
        currentPage: 1,
      }));
      setFormData(initialFormData);
    } else {
      showToast(response.msg, LABEL.TOAST_TYPE.ERROR);
    }

    setModalOpen(false);
    setSubmissionLoader(false);
  };

  const handleDeleteRecord = async (id) => {
    Swal.fire({
      title: "Are you sure you want to delete this Q&A?",
      showCancelButton: true,
      confirmButtonText: "Yes",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const deleteUrl = `${QNA}/${bot_id}/${id}`;
          const response = await CallAPI("USER", "DELETE", deleteUrl, {});
          if (response.status) {
            showToast(response.msg, LABEL.TOAST_TYPE.SUCCESS);
            const nextPage =
              QnAList.length === 1 && paginationData.currentPage > 1
                ? paginationData.currentPage - 1
                : paginationData.currentPage;
            const refreshedResponse = await getAllList(
              nextPage,
              paginationData.pageSize,
            );
            applyQnAResponse(refreshedResponse);
          } else {
            showToast(response.msg, LABEL.TOAST_TYPE.ERROR);
          }
        } catch (err) {
          showToast(err.msg, LABEL.TOAST_TYPE.ERROR);
        }
      }
    });
  };

  const handlePaginationChange = (event, value) => {
    SetPaginationData((prev) => ({ ...prev, currentPage: value }));
  };

  const handlePageSizeChange = (event) => {
    const nextPageSize =
      Number(event.target.value) || paginationObj.item_per_page;
    SetPaginationData((prev) => ({
      ...prev,
      currentPage: 1,
      pageSize: nextPageSize,
    }));
  };

  const downloadSample = async (id) => {
    const response = await CallAPI(
      "USER",
      "GET",
      `${SAMPLE_QNA_PROMPT_DOWNLOAD}/${id}`,
      null,
      null,
    );

    if (response.status) {
      const urlResponse = await fetch(response.res.data.downloadUrl);
      const blob = await urlResponse.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = "ReactJSNotesForProfessionals.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    }
  };

  const dataLoader = sampleLoading || qnaLoading;

  return (
    <div className="tab-content-grid">
      <div className="tab-content active">
        <div className="chatbot-history-grid">
          <header className="d-lg-flex align-items-center justify-content-between">
            <div className="qna-section-heading">
              <h4>Bot Questions and Answers</h4>
              <p>
                Add, review, and maintain the responses your chatbot uses for
                frequently asked questions.
              </p>
            </div>
            {!!permission.edit && (
              <div className="group-form d-md-flex">
                <a
                  href="#"
                  className="cta-primary"
                  onClick={(e) => {
                    e.preventDefault();
                    handleOpenAddModal();
                  }}
                >
                  <i className="fa-solid fa-sparkles" aria-hidden="true"></i>
                  UPLOAD Q&A
                </a>
                {props.finalStep == 2 && (
                  <a
                    href="#"
                    className="cta-primary"
                    onClick={(e) => {
                      e.preventDefault();
                      props.setFinalStep(3);
                      props.handleSetTab("4");
                    }}
                  >
                    Skip and Continue
                  </a>
                )}
              </div>
            )}
          </header>

          <div className={`modalOverlay ${modalOpen ? "modal-open" : ""}`}>
            <div className="modal-container width-sm modal-dark-bg">
              <a
                href="#"
                className="modalClose"
                onClick={(e) => {
                  e.preventDefault();
                  handleOnModalClose();
                }}
              >
                <img src="/images/icons/modal-close-icon.svg" alt="" />
              </a>
              <div className="modal-form-grid pt-5">
                <div className="field text-left">
                  <input
                    type="text"
                    value={formData.question}
                    onChange={(e) =>
                      setFormData({ ...formData, question: e.target.value })
                    }
                    className={`form-control ${
                      formData?.questionErr ? "is-invalid" : ""
                    }`}
                    name="question"
                    placeholder="Question"
                  />
                  {formData?.questionErr && (
                    <span>{formData?.questionErr}</span>
                  )}
                </div>
                <div className="field text-left">
                  <textarea
                    type="text"
                    value={formData.answer}
                    className={`form-control ${
                      formData?.answerErr ? "is-invalid" : ""
                    }`}
                    name="answer"
                    placeholder="Answer"
                    rows={5}
                    onChange={(e) =>
                      setFormData({ ...formData, answer: e.target.value })
                    }
                    ref={(el) => (textareaRefs.current.answer = el)}
                    onInput={() => handleInput("answer")}
                    style={{ overflow: "hidden", resize: "none" }}
                  />
                  {formData?.answerErr && <span>{formData?.answerErr}</span>}
                </div>
              </div>
              <div className="field text-center  mb-0">
                <button
                  className="cta-primary"
                  type="button"
                  disabled={submissionLoader}
                  onClick={() => handleAddUpdate()}
                >
                  {submissionLoader && (
                    <i className="fa-solid fa-spinner fa-spin"></i>
                  )}
                  {formData.id ? "Update" : "Add"} Q&A
                </button>
              </div>
            </div>
          </div>

          {dataLoader ? (
            <SplashScreen />
          ) : QnAList.length > 0 ? (
            <div className="documentation-grid_list">
              <section id="QNA" className="pt--80">
                <div className="container">
                  <div className="faqs-grid">
                    <div className="botname_block border rounded qna-tabs-wrap">
                      {QnAList?.map((item, index) => (
                        <article className="qna-card" key={index}>
                          <div className="qna-card-head">
                            <div className="qna-card-title-wrap">
                              <div className="qna-card-label">Question</div>
                              <h5 className="qna-card-title">
                                {item.question || ""}
                              </h5>
                            </div>
                            {!!permission.edit && (
                              <div className="action-group qna-card-actions">
                                <a href="#">
                                  <img
                                    src="/images/icons/edit-icon.svg"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleOpenEditModal(item);
                                    }}
                                  />
                                </a>
                                <a href="#">
                                  <img
                                    src="/images/icons/delete-icon.svg"
                                    alt=""
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleDeleteRecord(item.id);
                                    }}
                                  />
                                </a>
                              </div>
                            )}
                          </div>
                          <div className="qna-card-meta">
                            <div className="qna-meta-chip">
                              <span className="qna-meta-label">Updated</span>
                              <span className="qna-meta-value">
                                {new Date(item.updated_at).toLocaleString()}
                              </span>
                            </div>
                            <div className="qna-meta-chip">
                              <span className="qna-meta-label">By</span>
                              <span className="qna-meta-value">
                                {item.last_updated_by || "N/A"}
                              </span>
                            </div>
                          </div>
                          <div className="qna-card-body">
                            <div className="qna-card-label">Answer</div>
                            <div
                              className="qna-card-answer"
                              dangerouslySetInnerHTML={{
                                __html: item.answer,
                              }}
                            />
                          </div>
                        </article>
                      ))}
                    </div>
                  </div>
                </div>
              </section>
            </div>
          ) : (
            "No Q&A found"
          )}

          {paginationData.totalRecords > 0 && (
            <div className="pagination_wrap">
              <div className="pagination-info">
                Showing {paginationStart} to {paginationEnd} of {totalRecords}{" "}
                entries
              </div>
              <div className="pagination-table qna-pagination-controls">
                <div className="qna-page-size-control">
                  <span className="qna-page-size-label">Rows per page</span>
                  <div className="group-form qna-page-size-select-wrap">
                    <select
                      className="form-select qna-page-size-select"
                      value={paginationData.pageSize}
                      onChange={handlePageSizeChange}
                    >
                      {[10, 25, 50, 100].map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="qna-pagination-nav">
                  <Pagination
                    count={paginationData.totalPages}
                    page={paginationData.currentPage}
                    onChange={handlePaginationChange}
                    color="secondary"
                    shape="rounded"
                  />
                </div>
              </div>
            </div>
          )}

          {!sampleLoading && data?.qa ? (
            <div className="qa-prompt-sample-dowoload">
              <ul>
                {data.qa.link && (
                  <li onClick={() => downloadSample(data.qa.id)}>
                    <img src="/images/pdf-red-icon.png" alt="logo"></img>
                    <a href={data.qa.link} target="_blank">
                      Download Sample Q&A
                    </a>
                  </li>
                )}
              </ul>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
